import type { Appointment } from '../components/calendar/types';

interface CacheEntry {
  data: Appointment[];
  timestamp: number;
  weekNumber: number; // To detect week changes
  schemaVersion?: number;
}

const CACHE_KEY = 'dentec_week_appointments';
const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes - extended for page navigation
const CACHE_SCHEMA_VERSION = 3;
const IN_MEMORY_CACHE = {
  entry: null as CacheEntry | null,
  lastAccess: 0
};

/**
 * Gets the ISO week number for a date
 */
function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function isCompatibleCache(entry: CacheEntry): boolean {
  return entry.schemaVersion === CACHE_SCHEMA_VERSION
    && Array.isArray(entry.data)
    && entry.data.every(appointment => appointment.financialSummary !== undefined);
}

/**
 * Gets the week's start and end dates (Monday to Sunday) for a given date
 */
function getWeekBoundaries(referenceDate?: Date): { start: Date; end: Date } {
  const now = referenceDate || new Date();
  const dayOfWeek = now.getDay();
  const daysOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to get Monday
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() + daysOffset);
  weekStart.setHours(0, 0, 0, 0);
  
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);
  
  return { start: weekStart, end: weekEnd };
}

/**
 * Gets cached appointments for a specific week
 */
export function getCachedWeekAppointments(referenceDate?: Date): Appointment[] | null {
  try {
    const now = Date.now();
    const viewedWeek = getWeekNumber(referenceDate || new Date());
    
    // Check in-memory cache first (fastest - for page navigation)
    if (IN_MEMORY_CACHE.entry) {
      const age = now - IN_MEMORY_CACHE.entry.timestamp;
      if (IN_MEMORY_CACHE.entry.weekNumber === viewedWeek && age < CACHE_DURATION_MS && isCompatibleCache(IN_MEMORY_CACHE.entry)) {
        IN_MEMORY_CACHE.lastAccess = now;
        return IN_MEMORY_CACHE.entry.data;
      }
    }
    
    // Fall back to localStorage
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const entry: CacheEntry = JSON.parse(cached);
    const age = now - entry.timestamp;
    
    // Check if cache is still valid for the viewed week
    if (entry.weekNumber === viewedWeek && age < CACHE_DURATION_MS && isCompatibleCache(entry)) {
      // Restore to in-memory cache
      IN_MEMORY_CACHE.entry = entry;
      IN_MEMORY_CACHE.lastAccess = now;
      return entry.data;
    }
    
    // Cache expired, schema changed, or week changed; clear it
    clearAppointmentCache();
    return null;
  } catch (error) {
    console.error('Error reading appointment cache:', error);
    clearAppointmentCache();
    return null;
  }
}

/**
 * Stores appointments in cache (both in-memory and localStorage) for a specific week
 */
export function cacheWeekAppointments(appointments: Appointment[], referenceDate?: Date): void {
  try {
    const now = Date.now();
    const entry: CacheEntry = {
      data: appointments,
      timestamp: now,
      weekNumber: getWeekNumber(referenceDate || new Date()),
      schemaVersion: CACHE_SCHEMA_VERSION
    };
    
    // Store in both places
    IN_MEMORY_CACHE.entry = entry;
    IN_MEMORY_CACHE.lastAccess = now;
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch (error) {
    console.error('Error caching appointments:', error);
  }
}

/**
 * Clears the appointment cache
 */
export function clearAppointmentCache(): void {
  try {
    IN_MEMORY_CACHE.entry = null;
    IN_MEMORY_CACHE.lastAccess = 0;
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.error('Error clearing appointment cache:', error);
  }
}

/**
 * Invalidates cache on mutation (create/update/delete)
 */
export function invalidateAppointmentCache(): void {
  clearAppointmentCache();
}

/**
 * Checks if we should fetch fresh data
 */
export function shouldFetchAppointments(): boolean {
  const cached = getCachedWeekAppointments();
  return cached === null;
}

/**
 * Gets the week boundaries for API calls for a given date
 */
export function getWeekDateRange(referenceDate?: Date): { start: string; end: string } {
  const { start, end } = getWeekBoundaries(referenceDate);
  return {
    start: start.toISOString(),
    end: end.toISOString()
  };
}

/**
 * Filters appointments to only include a specific week
 */
export function filterToCurrentWeek(appointments: Appointment[], referenceDate?: Date): Appointment[] {
  const { start, end } = getWeekBoundaries(referenceDate);
  return appointments.filter(apt => {
    if (!apt.startTime) return false;
    const aptDate = new Date(apt.startTime);
    return aptDate >= start && aptDate <= end;
  });
}

/**
 * Checks if cached data is very fresh (less than 1 minute old)
 * Used to avoid refetch on page navigation
 */
export function isCacheVeryFresh(): boolean {
  if (!IN_MEMORY_CACHE.entry) return false;
  const age = Date.now() - IN_MEMORY_CACHE.entry.timestamp;
  return age < 60 * 1000; // Less than 1 minute
}
